import {
  AppConfigDataClient,
  StartConfigurationSessionCommand,
  GetLatestConfigurationCommand,
} from "@aws-sdk/client-appconfigdata";
import { fetchAuthSession } from 'aws-amplify/auth'

type AwsCredentials = {
  AccessKeyId: string;
  SecretKey: string;
  SessionToken: string;
};

type CognitoDetails = {
  UserPoolId: string;
  IdentityPoolId: string;
  UserPoolClientId: string;
};

async function getCredentials(): Promise<AwsCredentials> {
  const { credentials } = await fetchAuthSession();
  return {
    AccessKeyId: credentials?.accessKeyId || '',
    SecretKey: credentials?.secretAccessKey || '',
    SessionToken: credentials?.sessionToken || '',
  };
}
async function getCognitoDetails(): Promise<CognitoDetails> {
  return {
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    IdentityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID,
    UserPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
  };
}

export async function fetchFeatureFlags(): Promise<Record<string, any>> {
  try {
    // Step 1: Get Cognito details
    const cognitoDetails = await getCognitoDetails();
    // Step 2: Get temporary AWS credentials
    const credentials = await getCredentials();
    const { AccessKeyId, SecretKey, SessionToken } = credentials;
    const region = import.meta.env.VITE_REGION;

    if (!region) {
      throw new Error("Missing REGION environment variable");
    }

    // Step 3: Initialize AppConfig client
    const appConfig = new AppConfigDataClient({
      credentials: {
        accessKeyId: AccessKeyId,
        secretAccessKey: SecretKey,
        sessionToken: SessionToken,
      },
      region: region,
    });

    const applicationId = import.meta.env.VITE_APPLICATION_ID;
    const environmentId = import.meta.env.VITE_ENVIRONMENT_ID; // Fixed typo
    const configurationId = import.meta.env.VITE_CONFIGURATION_ID;

    if (!applicationId || !environmentId || !configurationId) {
      throw new Error("Missing required AppConfig environment variables");
    }

    // Step 4: Start configuration session
    const startSessionCommand = new StartConfigurationSessionCommand({
      ApplicationIdentifier: applicationId,
      EnvironmentIdentifier: environmentId,
      ConfigurationProfileIdentifier: configurationId,
    });
    const session = await appConfig.send(startSessionCommand);

    if (!session.InitialConfigurationToken) {
      throw new Error("Failed to retrieve configuration session token");
    }

    // Step 5: Fetch the configuration data
    const getConfigCommand = new GetLatestConfigurationCommand({
      ConfigurationToken: session.InitialConfigurationToken,
    });
    const configData = await appConfig.send(getConfigCommand);

    if (!configData.Configuration) {
      throw new Error("No configuration data received");
    }

    // Step 6: Parse and return feature flags
    const configString = new TextDecoder("utf-8").decode(configData.Configuration);
    const featureFlags = JSON.parse(configString);
    return featureFlags;
  } catch (error) {
    console.error("Error fetching feature flags from AppConfig:", error);
    throw error;
  }
}

